import { get, set } from 'idb-keyval';

/**
 * v5 — Paramètres de personnalisation du popup de personnalisation produit
 * (le popup où le client choisit ses sauces, options, etc.)
 *
 * IMPORTANT: Le composant ne touche QUE au visuel.
 * La logique métier (sélections, validations, étapes) reste 100% inchangée.
 */

export type SpecPopupTemplate =
  | 'classic'
  | 'elegant'
  | 'nature'
  | 'ocean'
  | 'minimal'
  | 'custom';

export type CornerRadius = 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
export type AnimationType = 'fade' | 'scale' | 'slide-up' | 'zoom-bounce';

export interface SpecPopupDesignSettings {
  // Toggle global : si false, design par défaut (orange) inchangé
  enabled: boolean;
  // Template de base
  template: SpecPopupTemplate;
  // Couleurs
  bgColor1: string;       // gradient début (fond du popup)
  bgColor2: string;       // gradient fin
  textColor: string;      // texte principal
  accentColor: string;    // couleur d'accent (barre de progression, "Suivant", boutons sélectionnés)
  // Image de fond
  backgroundImage: string | null;
  backgroundOpacity: number;     // 0-100
  backgroundBlur: number;        // 0-20
  // Formes
  popupRadius: CornerRadius;
  buttonRadius: CornerRadius;
  // Animation d'entrée
  enterAnimation: AnimationType;
  // Style des boutons d'option (avec auto-contraste)
  optionStyleMode: 'auto' | 'manual';  // auto = on calcule pour rester lisible
}

const STORAGE_KEY = 'specPopupDesignSettings';

export const DEFAULT_SPEC_DESIGN: SpecPopupDesignSettings = {
  enabled: false,            // OFF par défaut → comportement actuel intact
  template: 'classic',
  bgColor1: '#ffffff',
  bgColor2: '#ffffff',
  textColor: '#1f2937',
  accentColor: '#f97316',
  backgroundImage: null,
  backgroundOpacity: 15,
  backgroundBlur: 4,
  popupRadius: '2xl',
  buttonRadius: 'lg',
  enterAnimation: 'scale',
  optionStyleMode: 'auto',
};

/**
 * 5 templates pré-faits.
 */
export const SPEC_TEMPLATES: Record<
  Exclude<SpecPopupTemplate, 'custom'>,
  Partial<SpecPopupDesignSettings>
> = {
  classic: {
    template: 'classic',
    bgColor1: '#ffffff',
    bgColor2: '#ffffff',
    textColor: '#1f2937',
    accentColor: '#f97316',
  },
  elegant: {
    template: 'elegant',
    bgColor1: '#1f2937',
    bgColor2: '#111827',
    textColor: '#ffffff',
    accentColor: '#fbbf24',
    popupRadius: 'xl',
  },
  nature: {
    template: 'nature',
    bgColor1: '#16a34a',
    bgColor2: '#84cc16',
    textColor: '#ffffff',
    accentColor: '#ffffff',
  },
  ocean: {
    template: 'ocean',
    bgColor1: '#0891b2',
    bgColor2: '#2563eb',
    textColor: '#ffffff',
    accentColor: '#cffafe',
  },
  minimal: {
    template: 'minimal',
    bgColor1: '#fafafa',
    bgColor2: '#f3f4f6',
    textColor: '#111827',
    accentColor: '#000000',
    popupRadius: 'lg',
  },
};

export function applySpecTemplate(
  current: SpecPopupDesignSettings,
  template: Exclude<SpecPopupTemplate, 'custom'>
): SpecPopupDesignSettings {
  return {
    ...current,
    ...SPEC_TEMPLATES[template],
    template,
  };
}

export async function getSpecPopupDesign(): Promise<SpecPopupDesignSettings> {
  try {
    const stored = await get<SpecPopupDesignSettings>(STORAGE_KEY);
    if (stored && typeof stored === 'object') {
      return { ...DEFAULT_SPEC_DESIGN, ...stored };
    }
  } catch (e) {
    console.error('[SpecPopupDesign] load error', e);
  }
  return DEFAULT_SPEC_DESIGN;
}

export async function saveSpecPopupDesign(
  settings: SpecPopupDesignSettings
): Promise<boolean> {
  try {
    await set(STORAGE_KEY, settings);
    window.dispatchEvent(
      new CustomEvent('specPopupDesignUpdated', { detail: settings })
    );
    return true;
  } catch (e) {
    console.error('[SpecPopupDesign] save error', e);
    return false;
  }
}

/* ===========================================================================
 * Helpers de style
 * =========================================================================== */

export const RADIUS_CLASSES: Record<CornerRadius, string> = {
  none: 'rounded-none',
  sm: 'rounded',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  '2xl': 'rounded-2xl',
  full: 'rounded-3xl',
};

export const ENTER_ANIMATION_CLASSES: Record<AnimationType, string> = {
  fade: 'spec-anim-fade',
  scale: 'spec-anim-scale',
  'slide-up': 'spec-anim-slide-up',
  'zoom-bounce': 'spec-anim-zoom-bounce',
};

/**
 * Calcule la luminance d'une couleur hex (0 = noir, 1 = blanc).
 * Permet de savoir si on a un fond clair ou sombre.
 */
export function getLuminance(hex: string): number {
  const h = hex.replace('#', '');
  if (h.length < 6) return 0.5;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

export function isLightColor(hex: string): boolean {
  return getLuminance(hex) > 0.6;
}

/**
 * Auto-contraste : retourne les styles pour les boutons d'options en fonction
 * de la luminance du fond. Garantit la lisibilité.
 */
export function getOptionButtonStyles(
  bgColor1: string,
  accentColor: string,
  isSelected: boolean
): {
  background: string;
  border: string;
  color: string;
} {
  const lightBg = isLightColor(bgColor1);

  if (isSelected) {
    // Sélectionné : utilise l'accent
    const accentLight = isLightColor(accentColor);
    return {
      background: accentLight ? accentColor : `${accentColor}33`, // 33 = 20% alpha
      border: accentColor,
      color: accentLight ? '#1f2937' : accentColor,
    };
  }

  // Non sélectionné : adapte au fond
  if (lightBg) {
    return {
      background: 'rgba(0,0,0,0.04)',
      border: 'rgba(0,0,0,0.12)',
      color: '#374151',
    };
  } else {
    return {
      background: 'rgba(255,255,255,0.12)',
      border: 'rgba(255,255,255,0.2)',
      color: '#f9fafb',
    };
  }
}
